import type {
  AiAlternativeAdRequest,
  AiAlternativeMediaRequest,
  AiGenerationBatchRequest,
  AiGenerationBatchResponse,
  AiGenerationInputAsset,
  AiGenerationOptions,
  AiImageEditRequest,
  AiVideoGenRequest,
  DetectLanguageRequest,
  DetectLanguageResponse,
  ImageFeaturesResponse,
  TranslateLanguageRequest,
  TranslateLanguageResponse,
} from '@dashhudson/api-client/library-intelligence/model';

import { merge } from 'lodash-es';

import type { HttpResponse } from '@/types/http';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.libraryIntelligenceApiUrl, {
  camelizeKeys: true,
});

/**
 * Get visual features string from image data
 *
 * Expects a base64 encoding for a jpg image (excluding MIME type).
 * Uses a HTML form content-type rather than JSON, per intelligence's endpoint.
 */
export async function vectoriseV2(
  { imageData: img }: { imageData: string },
  config: HttpRequestConfig = {},
) {
  return http.post<ImageFeaturesResponse>(
    `/vectorise_v2`,
    { img },
    merge({ headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }, config),
  );
}

export async function detectTextLanguage(
  { texts }: Pick<DetectLanguageRequest, 'texts'>,
  config: HttpRequestConfig = {},
) {
  return http.post<DetectLanguageResponse>(`/detect_language`, { texts }, config);
}

export async function translateTextLanguage(
  { texts }: Pick<TranslateLanguageRequest, 'texts'>,
  config: HttpRequestConfig = {},
) {
  return http.post<TranslateLanguageResponse>(`/translate_language`, { texts }, config);
}

export type GenerateAiAlternativesParams = Pick<
  AiAlternativeMediaRequest,
  'brandId' | 'mediaId' | 'numberOfMediaToGenerate' | 'referenceUrl' | 'mode'
> & {
  /** Maps to `parentId` on the wire. */
  parentAlternativeId?: AiAlternativeMediaRequest['parentId'];
  socketId?: string;
};

export async function generateAiAlternatives(
  {
    brandId,
    mediaId,
    numberOfMediaToGenerate = 8,
    referenceUrl,
    parentAlternativeId = null,
    mode = 'creative',
    socketId,
  }: GenerateAiAlternativesParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/ai_alternative_media`,
    {
      brandId,
      mediaId,
      numberOfMediaToGenerate,
      mode,
      ...(referenceUrl && { referenceUrl }),
      ...(parentAlternativeId && { parentId: parentAlternativeId }),
      ...(socketId && { socketId }),
    },
    config,
  );
}

export type GenerateAiAlternativeAdsParams = Pick<
  AiAlternativeAdRequest,
  | 'brandId'
  | 'adPlatform'
  | 'sourceAdId'
  | 'sourceCampaignId'
  | 'sourceAdAccountId'
  | 'thumbnailUrl'
> & {
  socketId?: string;
};

export async function generateAiAlternativeAds(
  {
    brandId,
    adPlatform,
    sourceAdId,
    sourceCampaignId,
    sourceAdAccountId,
    thumbnailUrl,
    socketId,
  }: GenerateAiAlternativeAdsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/ai_alternative_ads`,
    {
      brandId,
      adPlatform,
      sourceAdId,
      sourceCampaignId,
      sourceAdAccountId,
      thumbnailUrl,
      mode: 'strict' as const,
      ...(socketId && { socketId }),
    },
    config,
  );
}

/**
 * Wrapper-side `generationOptions`. Widens `aspectRatios` to plain `string[]`;
 * the spec narrows to a literal union but callers pass user-supplied strings.
 */
export type CreateAiGenerationBatchOptions = Omit<AiGenerationOptions, 'aspectRatios'> & {
  aspectRatios?: string[] | null;
};

export type CreateAiGenerationBatchParams = Pick<
  AiGenerationBatchRequest,
  'brandId' | 'userId' | 'galleryId' | 'prompt'
> & {
  inputAssets?: AiGenerationInputAsset[];
  generationOptions?: CreateAiGenerationBatchOptions;
};

export async function createAiGenerationBatch(
  {
    brandId,
    userId,
    galleryId,
    prompt,
    inputAssets,
    generationOptions,
  }: CreateAiGenerationBatchParams,
  config: HttpRequestConfig = {},
): Promise<HttpResponse<AiGenerationBatchResponse>> {
  return http.post<AiGenerationBatchResponse>(
    `/ai_generation_batches`,
    {
      brandId,
      userId,
      galleryId,
      ...(prompt && { prompt }),
      ...(inputAssets && { inputAssets }),
      ...(generationOptions && { generationOptions }),
    },
    config,
  );
}

export type EditAiImageParams = Pick<
  AiImageEditRequest,
  'brandId' | 'instruction' | 'imageUrls' | 'sessionId' | 'replayFromTurn'
> & {
  /** Socket ID for async flow; when provided, returns 202 with sessionId. */
  socketId?: string;
};

export async function editAiImage(
  { brandId, instruction, imageUrls, sessionId, replayFromTurn, socketId }: EditAiImageParams,
  config: HttpRequestConfig = {},
) {
  // Sync flow returns broader fields (parts, turnNumber) than the spec, so we
  // leave the response untyped and let callers narrow.
  return http.post(
    `/ai_image_edit`,
    {
      brandId,
      instruction,
      imageUrls,
      ...(env.mode === 'production' && { resolution: '2K' as const }),
      ...(sessionId && { sessionId }),
      ...(replayFromTurn && { replayFromTurn }),
      ...(socketId && { socketId }),
    },
    config,
  );
}

export type GenerateMotionVideoParams = Pick<
  AiVideoGenRequest,
  'brandId' | 'instruction' | 'imageUrl' | 'sessionId' | 'replayFromTurn'
> & {
  /** Parent session ID for traceability (mapped to `parentSessionId` on the wire). */
  imageEditSessionId?: AiVideoGenRequest['parentSessionId'];
  /** Socket.IO connection ID for async result delivery. */
  socketId: string;
  /** Gallery to attach the generated video to. When provided, the backend saves media server-side and echoes mediaId/galleryMediaId in the VIDEO_GENERATED event (sc-199836). */
  galleryId?: number;
};

export async function generateMotionVideo(
  {
    brandId,
    instruction,
    imageUrl,
    sessionId,
    imageEditSessionId,
    socketId,
    replayFromTurn,
    galleryId,
  }: GenerateMotionVideoParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/ai_video_gen`,
    {
      brandId,
      instruction,
      socketId,
      ...(imageUrl && { imageUrl }),
      ...(sessionId && { sessionId }),
      ...(imageEditSessionId && { parentSessionId: imageEditSessionId }),
      ...(replayFromTurn != null && { replayFromTurn }),
      ...(galleryId != null && { galleryId }),
    },
    config,
  );
}
